/**
 * Minimal shape needed to evaluate the concierge gate. Decoupled from the full
 * generated `Dispensaries` type so this hook works for typed (.tsx) and
 * untyped (.jsx) callers alike — mirrors `is-order-type-takeover-enabled.ts`.
 * `conciergeChannels.web` accounts for the super-admin enterprise Concierge AI
 * setting AND the store's web `enableConcierge` toggle; missing/null fails closed.
 */
type DispensaryWithConciergeSetting = {
  id?: string | null;
  conciergeChannels?: { web?: boolean | null } | null;
} | null;

/**
 * Minimal UI-store shape this hook needs. Deliberately structural (not
 * `UIStore` itself) so callers can pass the real `useUI()` instance, a mocked
 * object in tests, or any other object with these three booleans.
 */
type UIConciergeGateFields = {
  isEmbedded: boolean;
  isKiosk: boolean;
  isStoreFront: boolean;
};

type UseIsConciergeEnabledOptions = {
  /**
   * Whether this call site's surface is allowed to render on embedded (iframe)
   * menus at all. Defaults to `true` (the nav button case). The FAB passes
   * `false`: per product decision the fixed-position floating button never
   * shows on embedded menus, because a fixed element does not behave reliably
   * inside an auto-resized, host-page-controlled iframe. Embedded coverage
   * below the 880px desktop-header breakpoint comes from ConciergeEntryBanner
   * instead (ECOMM-12462).
   */
  allowEmbedded?: boolean;
};

/**
 * Single source of truth for whether the AI Concierge (nav button + FAB) is
 * enabled for the current dispensary/route. Combines:
 *
 * - The `conciergeChannels.web` resolver field — accounts for the super-admin
 *   enterprise Concierge AI setting AND the store's web `enableConcierge`
 *   toggle. Missing/null fails closed.
 * - Storefront: enabled whenever the channel is on (`UI.isStoreFront`).
 * - Embedded (iframe): additionally requires `options.allowEmbedded` (default
 *   `true`) — the embedded surface now shares the same admin gate as
 *   storefront, with no separate rollout flag (ECOMM-12301 shipped).
 * - Kiosk is explicitly excluded from the embedded branch. `UI.isKiosk` and
 *   `UI.isEmbedded` are NOT mutually exclusive as raw booleans — only the
 *   derived `UI.variant` getter orders them (a kiosk reverse-proxied inside an
 *   iframe has both `true` simultaneously, see ECOMM-7763) — so without this
 *   explicit exclusion, an iframed kiosk with `conciergeChannels.web` on would
 *   incorrectly render the concierge on the kiosk display.
 *
 * ECOMM-12299 / ECOMM-12301.
 */
export function useIsConciergeEnabled(
  dispensary: DispensaryWithConciergeSetting,
  ui: UIConciergeGateFields,
  options: UseIsConciergeEnabledOptions = {}
): boolean {
  const { allowEmbedded = true } = options;

  return (
    Boolean(dispensary?.conciergeChannels?.web) && (ui.isStoreFront || (allowEmbedded && ui.isEmbedded && !ui.isKiosk))
  );
}
